import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { AuthBrand } from '../components/AuthBrand';
import { LoadingScreen } from '../components/LoadingScreen';
import { PLATFORMS, platformsForUser } from '../lib/platforms';

function PlatformSelector({
  heading,
  platforms,
}: {
  heading: string;
  platforms: typeof PLATFORMS;
}) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AuthBrand />
        <div className="card p-8">
          <h1 className="text-xl font-semibold text-center mb-2">Where would you like to go?</h1>
          <p className="text-sm text-foreground-secondary text-center mb-6">{heading}</p>
          <div className="flex flex-col gap-3">
            {platforms.map(({ label, description, url, icon: Icon }) => (
              <a
                key={url}
                href={url}
                className="flex items-center gap-4 p-4 rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-accent" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-foreground">{label}</p>
                  <p className="text-xs text-foreground-secondary">{description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function HomePage() {
  const { user, isLoading, logout } = useAuth();

  if (isLoading) return <LoadingScreen />;

  // Auth Centre admins go straight to the admin panel.
  if (user?.role === 'ADMIN') {
    return <Navigate to="/admin/applications" replace />;
  }

  const visiblePlatforms = platformsForUser(user?.email);

  // Authenticated non-admin users: show platform selector + sign-out option so
  // they can navigate to the right platform or clear the session.
  if (user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <AuthBrand />
          <div className="card p-8">
            <h1 className="text-xl font-semibold text-center mb-2">Where would you like to go?</h1>
            <p className="text-sm text-foreground-secondary text-center mb-1">
              Signed in as <span className="text-foreground font-medium">{user.email}</span>
            </p>
            <p className="text-xs text-foreground-muted text-center mb-6">
              Select a platform — you'll be signed in automatically.
            </p>
            <div className="flex flex-col gap-3 mb-4">
              {visiblePlatforms.map(({ label, description, url, icon: Icon }) => (
                <a
                  key={url}
                  href={url}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-accent" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-foreground">{label}</p>
                    <p className="text-xs text-foreground-secondary">{description}</p>
                  </div>
                </a>
              ))}
            </div>
            <button className="btn-secondary w-full" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Unauthenticated: platform selector — each platform initiates its own SSO
  // flow with the correct client_id, so auth.tyaus.com never authenticates a
  // user without knowing which platform they are targeting.
  return (
    <PlatformSelector
      heading="Select your platform to sign in. You'll be redirected to authenticate."
      platforms={PLATFORMS}
    />
  );
}
